import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-translate-plus';
import styled from 'styled-components';

import styles from './ContactForm.module.scss';

import {
  Button,
  Dialog,
  DropDownField,
  FlyoutActionButton,
  InputField,
  ModalPortal,
  OptionFlyout,
  OptionFlyoutItem,
  Paragraph,
  TextField,
} from '@hoffmann/pattern-library-2';

import { SESSION_KEY, useSessionStorage } from '../../hooks/useSessionStorage';
import { AllSupportedCountries, getCountryImage } from '../../services/locale';
import { TranslationFunction, useLocalisation } from '../../services/localisationContext';
import { withStyledClasses } from '../../styled/withStyledClasses';
import { Checkbox } from './Checkbox';
import { shouldScrollToCheckboxErrors } from './ContactForm';
import {
  emailRequiredValidator,
  getValidZipExample,
  isPhoneCallsAcceptedValidator,
  stringRequiredValidator,
  zipValidator,
} from './Validators';
import { countries, supportedCountries, SupportedCountries } from './supportedCountries';

export interface PublicContactFormValues {
  salutation: string;
  salutationValue: string;
  user?: string | null;
  firstName: string;
  lastName: string;
  zip: string;
  city: string;
  country: '' | SupportedCountries;
  customerNumber: string;
  customerName: string;
  email: string;
  phone: string;
  message: string;
  isPhoneCallsAccepted: boolean;
}

type ContactFormErrors = Partial<Record<keyof PublicContactFormValues, string>>;
type ValidationFunction<T> = (
  value: T,
  field?: string,
  data?: PublicContactFormValues,
) => string | undefined;
type ContactFormValidators = {
  [K in keyof PublicContactFormValues]?: ValidationFunction<PublicContactFormValues[K]>;
};

export interface PublicContactFormData {
  salutations: { male: string; female: string };
  countries: Record<SupportedCountries, string>;
  errors: ContactFormErrors;
  values: PublicContactFormValues;
  required: string[];
  disabled: string[];
  validators: ContactFormValidators;
}

export const getInitialPublicContactFormData = (
  t: TranslationFunction,
  lastFilledValues: PublicContactFormValues,
  selectedCountry: string,
): PublicContactFormData => {
  const result = {
    salutations: {
      male: t('LABEL_SALUTATION_MALE'),
      female: t('LABEL_SALUTATION_FEMALE'),
    },
    countries: supportedCountries(t),
    errors: {},
    values: {
      salutation: '',
      salutationValue: '',
      firstName: '',
      lastName: '',
      zip: '',
      city: '',
      country: selectedCountry,
      customerNumber: '',
      customerName: '',
      email: '',
      phone: '',
    },
    required: ['firstName', 'lastName', 'zip', 'city', 'country', 'email', 'isPhoneCallsAccepted'],
    disabled: [],
    validators: {
      firstName: stringRequiredValidator(t),
      lastName: stringRequiredValidator(t),
      zip: zipValidator(t),
      city: stringRequiredValidator(t),
      country: stringRequiredValidator(t),
      email: emailRequiredValidator(t),
      isPhoneCallsAccepted: isPhoneCallsAcceptedValidator(t),
    },
  };
  return {
    ...result,
    values: {
      ...result.values,
      ...lastFilledValues,
      message: '',
      isPhoneCallsAccepted: false,
    },
  };
};

const noop = () => {};

interface Props {
  formData: PublicContactFormData;
  onSubmit: (dto: PublicContactFormValues) => void;
  onClose: () => void;
  onChange?: (dto: PublicContactFormData) => void;
}

export const PublicContactForm: React.FC<Props> = ({ formData, onSubmit, onClose, onChange }) => {
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [, setLastUsedFormData] = useSessionStorage<PublicContactFormValues>(
    SESSION_KEY.CONFIGURATOR_PUBLIC_CONTACT_FORM_VALUES,
  );

  const [data, setData] = useState<PublicContactFormData>(formData);
  const { values, errors, validators, required, disabled } = data;
  const t = useTranslate('ContactForm');

  const { currentCountry } = useLocalisation();

  const privacyCheckbox = useRef<HTMLInputElement>(null);
  const phoneCheckbox = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setData((current) => {
      if (!current.values.country) {
        const country = countries.find((c) => c === currentCountry) || ''; // WSC-2317: || ANOTHER_COUNTRY
        return { ...current, values: { ...current.values, country } };
      }
      return current;
    });
  }, [currentCountry]);

  const handleClose = useCallback(() => {
    setLastUsedFormData(data.values);
    onClose();
  }, [data, setLastUsedFormData, onClose]);

  const handleSubmit = () => {
    setLoading(true);
    Object.keys(validators).forEach((key) => {
      const validate = validators[key] || noop;
      errors[key] = validate(values[key], key, values);
    });
    const hasErrors = Object.keys(errors).some((key) => !!errors[key]);
    setData({
      ...data,
      errors: {
        ...errors,
      },
    });
    if (hasErrors) {
      setLoading(false);
      setShowErrors(hasErrors);
      if (shouldScrollToCheckboxErrors(data, ['isPhoneCallsAccepted'])) {
        privacyCheckbox?.current?.focus();
      }
      return;
    }
    data.values.salutation = data.salutations[data.values.salutationValue] || '';
    setLastUsedFormData(data.values);
    onSubmit(data.values);
  };

  const getPostfix = (field: string) => {
    return required.includes(field) ? '*' : '';
  };

  const handleChange = <T extends string | boolean>(field: string) => (value: T) => {
    updateData(field, value);
  };

  const updateData = (field: string, value: string | boolean) => {
    const validate = validators[field] || noop;
    const updated = {
      ...data,
      errors: {
        ...errors,
        [field]: validate(value, field, values),
      },
      values: {
        ...values,
        [field]: value,
      },
    };
    setData(updated);
    if (typeof onChange === 'function') {
      onChange(updated);
    }
  };

  return (
    <StyledModalPortal isOpen={true}>
      <Dialog
        classNames={[styles.dialog]}
        title={t('TITLE')}
        size={200}
        actions={[
          <Button key="cancelBtn" label={t('BUTTON_CANCEL')} ghost onClick={handleClose} />,
          <Button
            key="confirmBtn"
            label={t('BUTTON_SEND')}
            ghost
            theme="brand"
            onClick={handleSubmit}
            disabled={loading}
            loading={loading}
          />,
        ]}
      >
        <Paragraph>{t('DESCRIPTION')}</Paragraph>
        <div className={styles.table}>
          <DropDownField
            data-cy="dropDown-salutation"
            label={t('LABEL_SALUTATION')}
            value={values.salutationValue}
            onChange={handleChange('salutationValue')}
            error={showErrors ? errors.salutationValue : undefined}
            disabled={disabled.includes('salutationValue')}
          >
            <OptionFlyout>
              {Object.entries(data.salutations).map(([key, item]) => (
                <OptionFlyoutItem key={key} value={key} label={item} />
              ))}
            </OptionFlyout>
          </DropDownField>
          <div></div>
          <InputField label="Name" value={values.firstName} classNames={[styles.name]} />
          <InputField
            data-cy="inputField-firstName"
            label={`${t('LABEL_NAME_FIRST')}${getPostfix('firstName')}`}
            onChange={handleChange('firstName')}
            value={values.firstName}
            error={showErrors ? errors.firstName : undefined}
            disabled={disabled.includes('firstName')}
          />
          <InputField
            data-cy="inputField-lastName"
            label={`${t('LABEL_NAME_LAST')}${getPostfix('lastName')}`}
            onChange={handleChange('lastName')}
            value={values.lastName}
            error={showErrors ? errors.lastName : undefined}
            disabled={disabled.includes('lastName')}
          />
          <InputField
            data-cy="inputField-postCode"
            label={`${t('LABEL_ZIP')}${getPostfix('zip')}${getValidZipExample(values.country)}`}
            onChange={handleChange('zip')}
            value={values.zip}
            error={showErrors ? errors.zip : undefined}
            disabled={disabled.includes('zip')}
          />
          <InputField
            data-cy="inputField-city"
            label={`${t('LABEL_CITY')}${getPostfix('city')}`}
            onChange={handleChange('city')}
            value={values.city}
            error={showErrors ? errors.city : undefined}
            disabled={disabled.includes('city')}
          />
          <CountrySelection country={values.country}>
            <DropDownField
              data-cy="dropdown-country"
              label={`${t('LABEL_COUNTRY')}${getPostfix('country')}`}
              value={values.country}
              onChange={handleChange('country')}
              error={showErrors && !!errors.country}
              disabled={disabled.includes('country')}
            >
              <OptionFlyout>
                {Object.entries(data.countries).map(([key, item]) => (
                  <FlyoutActionButton
                    key={key}
                    value={key}
                    label={item}
                    icon={getCountryImage(AllSupportedCountries[key.toUpperCase()], '15')}
                  />
                ))}
              </OptionFlyout>
            </DropDownField>
          </CountrySelection>
          <div></div>
          <InputField
            label={`${t('LABEL_CUSTOMER_NUMBER')}${getPostfix('customerNumber')}`}
            onChange={handleChange('customerNumber')}
            value={values.customerNumber}
            error={showErrors ? errors.customerNumber : undefined}
            disabled={disabled.includes('customerNumber')}
          />
          <InputField
            label={`${t('LABEL_CUSTOMER_NAME')}${getPostfix('customerName')}`}
            onChange={handleChange('customerName')}
            value={values.customerName}
            error={showErrors ? errors.customerName : undefined}
            disabled={disabled.includes('customerName')}
          />
          <InputField
            data-cy="inputField-email"
            label={`${t('LABEL_EMAIL')}${getPostfix('email')}`}
            onChange={handleChange('email')}
            value={values.email}
            error={showErrors ? errors.email : undefined}
            disabled={disabled.includes('email')}
          />
          <InputField
            label={`${t('LABEL_PHONE')}${getPostfix('phone')}`}
            onChange={handleChange('phone')}
            value={values.phone}
            error={showErrors ? errors.phone : undefined}
            disabled={disabled.includes('phone')}
          />
        </div>
        <div>
          <TextField
            label={`${t('LABEL_MESSAGE')}${getPostfix('message')}`}
            maxLength={1000}
            onChange={handleChange('message')}
            value={values.message}
            error={showErrors ? errors.message : undefined}
            disabled={disabled.includes('message')}
          />
        </div>
        <div></div>
        <div id={'label-contact-customer'}>
          {t('FOOTER_TEXT')}
          &nbsp;
          {
            <a
              rel="noopener noreferrer"
              target="_blank"
              href={t('URL_PRIVACY')}
              className={styles.link}
            >
              {t('LINK_PRIVACY')}
            </a>
          }
        </div>
        {values.phone && (
          <Checkbox
            checked={values.isPhoneCallsAccepted}
            onChange={handleChange('isPhoneCallsAccepted')}
            error={showErrors ? errors.isPhoneCallsAccepted : undefined}
            ref={phoneCheckbox}
          >
            <div id={'label-contact-customer'}>
              {t('LABEL_CONTACT_CUSTOMER')}
              {getPostfix('isPhoneCallsAccepted')}
            </div>
          </Checkbox>
        )}
        <div></div>
      </Dialog>
    </StyledModalPortal>
  );
};

const StyledModalPortal = styled(withStyledClasses(ModalPortal))`
  .hpl2-Dialog--size-200 .hpl2-Dialog__header,
  .hpl2-Dialog--size-200 .hpl2-Dialog__content,
  .hpl2-Dialog--size-200 .hpl2-Dialog__footer {
    max-width: 756px;
    width: 756px;
  }

  .hpl2-Dialog--size-200 .hpl2-Dialog__header {
    border-bottom: 1px solid #e9e9e9;
    padding-bottom: 16px;
  }
`;
const CountrySelection = styled.div<{ country: string }>`
  ${({ country }) =>
    country &&
    `
    .hpl2-DropDownField__button {
      background-image: url('/assets/svg/${country}.svg');
      background-position: left 15px top 27px;
      background-repeat: no-repeat;
      padding-left: 3rem;
      background-size: 20px;
  `}
`;
